import { useMemo, useState } from 'react';
import './MembershipTypePerksEditor.css';

type MembershipTypePerksEditorProps = {
  perks: string[];
  onChange: (next: string[]) => void;
  error?: string;
};

const PRESET_PERKS = [
  'Priority Queue',
  'Exclusive Offers',
  'Free Parking',
  'Birthday Bonus',
  'Early Access',
  'Dedicated Support',
  'Member Discounts',
  'Bonus Points',
];

const MAX_PERKS = 10;

const normalizePerk = (value: string): string => value.trim().replace(/\s+/g, ' ');

const MembershipTypePerksEditor = ({ perks, onChange, error = '' }: MembershipTypePerksEditorProps) => {
  const [draftPerk, setDraftPerk] = useState('');

  const normalizedPerks = useMemo(
    () => perks.map(normalizePerk).filter(Boolean),
    [perks]
  );

  const addPerk = (value: string) => {
    const normalized = normalizePerk(value);
    if (!normalized) return;
    if (normalizedPerks.some((perk) => perk.toLowerCase() === normalized.toLowerCase())) return;
    if (normalizedPerks.length >= MAX_PERKS) return;
    onChange([...normalizedPerks, normalized]);
  };

  const removePerk = (target: string) => {
    onChange(normalizedPerks.filter((perk) => perk !== target));
  };

  const handleDraftSubmit = () => {
    addPerk(draftPerk);
    setDraftPerk('');
  };

  const remainingPresetPerks = PRESET_PERKS.filter(
    (preset) => !normalizedPerks.some((perk) => perk.toLowerCase() === preset.toLowerCase())
  );

  return (
    <section className="appointments-v2-card membership-perks-card">
      <div className="appointments-v2-card-header membership-perks-card__header">
        <div>
          <h3>Perks</h3>
          <p className="membership-perks-card__intro">Choose the benefits that make this membership worth joining and easy to explain.</p>
        </div>
        <span className="membership-perks-card__counter">{normalizedPerks.length}/{MAX_PERKS}</span>
      </div>
      <div className="appointments-v2-card-body membership-perks-card__body">
        <div className="membership-perks-card__selection">
          {normalizedPerks.length === 0 ? (
            <p className="membership-perks-card__empty">No perks added yet. Use presets below or type a custom perk.</p>
          ) : (
            <div className="membership-perks-card__pillGrid">
              {normalizedPerks.map((perk) => (
                <span key={perk} className="membership-perks-card__selectedPill">
                  <span>{perk}</span>
                  <button
                    type="button"
                    className="membership-perks-card__remove"
                    onClick={() => removePerk(perk)}
                    aria-label={`Remove ${perk}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="membership-perks-card__presetBlock">
          <p className="membership-perks-card__subheading">Quick add from presets</p>
          <div className="membership-perks-card__presetGrid">
            {remainingPresetPerks.map((preset) => (
              <button
                key={preset}
                type="button"
                className="membership-perks-card__presetChip"
                onClick={() => addPerk(preset)}
                disabled={normalizedPerks.length >= MAX_PERKS}
              >
                + {preset}
              </button>
            ))}
          </div>
        </div>

        <div className="membership-perks-card__customRow">
          <input
            className="input membership-perks-card__input"
            value={draftPerk}
            placeholder="Or type a custom perk..."
            maxLength={120}
            onChange={(event) => setDraftPerk(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleDraftSubmit();
              }
            }}
          />
          <button
            type="button"
            className="btn ghost"
            onClick={handleDraftSubmit}
            disabled={!normalizePerk(draftPerk) || normalizedPerks.length >= MAX_PERKS}
          >
            Add
          </button>
        </div>
        {error ? <span className="membership-perks-card__error">{error}</span> : null}
      </div>
    </section>
  );
};

export default MembershipTypePerksEditor;
